import React, { JSX } from "react";
import Button from "@mui/material/Button";
import moment from "moment/moment";
import { BookingStatus } from "../../Bookings/ManageBooking";
import useClientBookings from "../../../hooks/useClientBookings";
import { book, Type as BookingActionType, updateBooking } from "../../../actions/booking";
import { useAppDispatch } from "../../../store/hooks";
import { getClientVisits, Type } from "../../../actions/client";
import useGetOnGoing from "../../../hooks/useGetOnGoing";
import OkhatiDialog from "../../../components/Dialog/Dialog";

interface ClientCheckInOutProps {
  clientId: number;
}

function ClientCheckInOut({ clientId }: ClientCheckInOutProps): JSX.Element | null {
  const { todaysBooking } = useClientBookings({ clientId });
  const dispatch = useAppDispatch();
  const isLoading = useGetOnGoing(Type.GET_CLIENT_VISITS);
  const isSubmitting = useGetOnGoing([BookingActionType.MODIFY_BOOKING, BookingActionType.BOOK]);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [status, setStatus] = React.useState<BookingStatus | null>(null);

  const canCheckIn =
    !todaysBooking ||
    [BookingStatus.reserved, BookingStatus.confirmed, BookingStatus.engaged].includes(
      todaysBooking.status as BookingStatus
    );

  const canCheckOut =
    !!todaysBooking &&
    [BookingStatus.waiting, BookingStatus.handled].includes(todaysBooking.status as BookingStatus);

  const handleStatusChange = async () => {
    await dispatch(getClientVisits(clientId));
    setIsDialogOpen(false);
  };

  if (isLoading) return null;

  return (
    <>
      {canCheckIn && (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={async () => {
            setStatus(BookingStatus.waiting);
            setIsDialogOpen(true);
          }}
        >
          {todaysBooking ? "Check In Client" : "Create & Check In"}
        </Button>
      )}

      {canCheckOut && (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={async () => {
            if (todaysBooking.id) {
              setStatus(BookingStatus.checkedOut);
              setIsDialogOpen(true);
            }
          }}
        >
          Check Out Client
        </Button>
      )}
      <OkhatiDialog
        open={isDialogOpen}
        isLoading={isSubmitting}
        title="Are you sure ?"
        description={`This will change client's booking status to ${
          status === BookingStatus.waiting ? "checked In/ Waiting" : "Checked Out"
        }.`}
        readMode={false}
        next={async () => {
          if (status) {
            if (todaysBooking?.id) {
              await dispatch(updateBooking(todaysBooking.id, { status }));
              await handleStatusChange();
            } else {
              await dispatch(
                book(
                  {
                    clientId,
                    status: BookingStatus.waiting,
                    bookingType: "regular",
                    resourceCentreId: todaysBooking?.resourceCentreId,
                    start: moment().toISOString(),
                    end: moment().add("15", "minute").toISOString(),
                    sendToSp: false,
                    sendToClient: false,
                    throughPublic: false,
                    visitType: "clinic",
                    remarks: "booked on arrival"
                  },
                  true
                )
              );
              await handleStatusChange();
            }
          }
        }}
        cancel={() => setIsDialogOpen(false)}
        nextButtonText="Yes"
      />
    </>
  );
}

export default ClientCheckInOut;
